import React, { useState, useEffect, useRef } from 'react';
import { FaCalendarAlt, FaMapMarkerAlt, FaDownload } from 'react-icons/fa';
import { fetchTripById } from '../../functions/FetchTrips';
import '../../styles/Itinerary.css';
import ItineraryGallery from './ItineraryGallery';
import ItineraryTabs from './ItineraryTabs';
import ItineraryOverview from './ItineraryOverview';
import ItineraryTimeline from './ItineraryTimeline';
import ItineraryDetails from './ItineraryDetails';
import ItineraryNotes from './ItineraryNotes';
import ItineraryCancellation from './ItineraryCancellation';
import ItineraryBookingCard from './ItineraryBookingCard';
import Header from '../Header';
import Footer from '../Footer';

const Itinerary = ({ tripId }) => {
  const [tourData, setTourData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeDay, setActiveDay] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview-section');
  const [activeMonth, setActiveMonth] = useState(null);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState(0);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isBookingFormOpen, setIsBookingFormOpen] = useState(false);

  const overviewRef = useRef(null);
  const itineraryRef = useRef(null);
  const detailsRef = useRef(null);
  const notesRef = useRef(null);
  const cancellationRef = useRef(null);
  const tabsRef = useRef(null);

  useEffect(() => {
    const loadTripData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (!tripId) {
          throw new Error('Trip ID is required');
        }
        
        const tripData = await fetchTripById(tripId);
        
        if (!tripData) {
          throw new Error('Trip not found');
        }
        
        setTourData(tripData);
      } catch (err) {
        setError(err.message || "Failed to load trip details.");
      } finally {
        setLoading(false);
      }
    };

    loadTripData();
  }, [tripId]);

  const processFetchedData = (data) => {
    if (!data) return null;

    const galleryImages = [
      { src: data.cover_image_url, alt: data.tripname },
      ...(data.additional_image_url || []).map((url, index) => ({ src: url, alt: `${data.tripname} - Image ${index + 1}` }))
    ].filter(img => img.src);

    const pricingCategories = data.starting_price ? Object.entries(data.starting_price).map(([type, price]) => ({
      type: type.replace(/_/g, ' '),
      price: Number(price),
      discount: false
    })) : [];

    const lowestPriceIndex = pricingCategories.length > 0
      ? pricingCategories.reduce((lowestIdx, category, idx, arr) =>
          category.price < arr[lowestIdx].price ? idx : lowestIdx, 0)
      : 0;

    const tourBatches = data.batches || {};
    const allMonths = Object.keys(tourBatches);
    const visibleMonthsCount = 5;
    const visibleMonths = allMonths.slice(0, visibleMonthsCount);
    const hiddenMonths = allMonths.slice(visibleMonthsCount);

    let itineraryDays = [];
    if (data.Itinerary && typeof data.Itinerary === 'object' && !Array.isArray(data.Itinerary)) {
      itineraryDays = Object.entries(data.Itinerary)
        .map(([dayKey, dayData]) => {
          const dayMatch = dayKey.match(/^day\s?(\d+)$/i);
          let meals = { B: false, L: false, D: false };
          if (dayData.meals) {
            if ('B' in dayData.meals || 'L' in dayData.meals || 'D' in dayData.meals) {
              meals = {
                B: !!dayData.meals.B,
                L: !!dayData.meals.L,
                D: !!dayData.meals.D
              };
            } else {
              meals = {
                B: !!dayData.meals.breakfast,
                L: !!dayData.meals.lunch,
                D: !!dayData.meals.dinner
              };
            }
          }
          return {
            day: dayMatch ? parseInt(dayMatch[1], 10) : 0,
            title: dayData.title || dayKey,
            description: dayData.description || '',
            activities: dayData.activities || [],
            meals,
            accommodation: dayData.accommodation || 'N/A',
            image: dayData.image || data.cover_image_url || ''
          };
        })
        .filter(day => day.day > 0)
        .sort((a, b) => a.day - b.day);
    } else if (Array.isArray(data.Itinerary)) {
      itineraryDays = data.Itinerary.map((dayData, index) => {
        let meals = { B: false, L: false, D: false };
        if (dayData.meals) {
          if ('B' in dayData.meals || 'L' in dayData.meals || 'D' in dayData.meals) {
            meals = {
              B: !!dayData.meals.B,
              L: !!dayData.meals.L,
              D: !!dayData.meals.D
            };
          } else {
            meals = {
              B: !!dayData.meals.breakfast,
              L: !!dayData.meals.lunch,
              D: !!dayData.meals.dinner
            };
          }
        }
        return {
          day: dayData.day || index + 1,
          title: dayData.title || `Day ${index + 1}`,
          description: dayData.description || '',
          activities: dayData.activities || [],
          meals,
          accommodation: dayData.accommodation || 'N/A',
          image: dayData.image || data.cover_image_url || ''
        };
      }).sort((a, b) => a.day - b.day);
    }

    const notes = data.notes || [];
    const cancellationPolicy = data.cancellationPolicy || [];

    const displayPrice = data.display_price || (pricingCategories.length > 0 ? pricingCategories[lowestPriceIndex].price : 0);
    const discountPercent = data.discount_percentage || 0;
    const originalPrice = (displayPrice && discountPercent > 0)
        ? Math.round(displayPrice / (1 - discountPercent / 100))
        : displayPrice;

    return {
      ...data,
      title: data.tripname,
      duration: data.days ? `${data.days} Days / ${data.days - 1} Nights` : 'N/A',
      destinations: data.location ? data.location.split(',').map(s => s.trim()) : [],
      overview: data.description,
      galleryImages,
      pricingCategories,
      lowestPriceIndex,
      tourBatches,
      allMonths,
      visibleMonths,
      hiddenMonths,
      days: itineraryDays,
      inclusions: data.inclusions || [],
      exclusions: data.exclusions || [],
      notes,
      cancellationPolicy,
      originalPrice,
      discountPercent,
      displayPrice
    };
  };

  const processedTourData = processFetchedData(tourData);

  const formatPrice = (price) => {
    return price.toLocaleString('en-IN');
  };

  const openModal = (index) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const showNextImage = (e) => {
    e.stopPropagation();
    if (!processedTourData || processedTourData.galleryImages.length === 0) return;
    setSelectedImageIndex((prevIndex) => (prevIndex + 1) % processedTourData.galleryImages.length);
  };

  const showPrevImage = (e) => {
    e.stopPropagation();
    if (!processedTourData || processedTourData.galleryImages.length === 0) return;
    setSelectedImageIndex((prevIndex) => (prevIndex - 1 + processedTourData.galleryImages.length) % processedTourData.galleryImages.length);
  };

  const handleDayClick = (day) => {
    setActiveDay(day);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const stickyElementHeight = tabsRef.current?.offsetHeight || 60;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - stickyElementHeight - 20;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const handleMonthSelect = (month) => {
    setActiveMonth(month);
    setIsMoreDropdownOpen(false);
  };

  const getShortMonth = (fullMonth) => {
    return fullMonth.split(' ')[0].slice(0, 3);
  };

  const handlePricingSelect = (index) => {
    setSelectedPricing(index);
  };

  const handleBatchSelect = (date) => {
    setSelectedBatch(date);
  };

  const toggleBookingForm = () => {
    setIsBookingFormOpen(!isBookingFormOpen);
  };

  useEffect(() => {
    if (loading || !processedTourData) return;

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveTab(prevTab => {
            if (prevTab !== entry.target.id) {
              return entry.target.id;
            }
            return prevTab;
          });
        }
      });
    };

    const stickyElementHeight = tabsRef.current?.offsetHeight || 60;
    const rootMargin = `-${stickyElementHeight + 25}px 0px -45% 0px`;

    const observerOptions = {
      root: null,
      rootMargin: rootMargin,
      threshold: 0
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = [overviewRef.current, itineraryRef.current, detailsRef.current, notesRef.current, cancellationRef.current].filter(Boolean);
    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
      observer.disconnect();
    };
  }, [loading, processedTourData]);

  useEffect(() => {
    if (processedTourData && processedTourData.allMonths && processedTourData.allMonths.length > 0 && !activeMonth) {
      setActiveMonth(processedTourData.allMonths[0]);
    }
    if (processedTourData && processedTourData.allMonths && activeMonth && !processedTourData.allMonths.includes(activeMonth)) {
      setActiveMonth(processedTourData.allMonths.length > 0 ? processedTourData.allMonths[0] : null);
    }
  }, [processedTourData, activeMonth]);

  if (loading) {
    return (
      <>
        <div className="itinerary-header-wrapper"><Header /></div>
        <div className="itinerary-container"><div className="loading-message">Loading Trip Details...</div></div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="itinerary-header-wrapper"><Header /></div>
        <div className="itinerary-container"><div className="error-message">Error: {error}</div></div>
        <Footer />
      </>
    );
  }

  if (!processedTourData) {
    return (
      <>
        <div className="itinerary-header-wrapper"><Header /></div>
        <div className="itinerary-container"><div className="error-message">Trip data could not be loaded.</div></div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <div className="itinerary-header-wrapper dark-theme">
        <Header />
      </div>
      <div className="itinerary-container dark-theme">
        <ItineraryGallery
          galleryImages={processedTourData.galleryImages}
          isModalOpen={isModalOpen}
          selectedImageIndex={selectedImageIndex}
          openModal={openModal}
          closeModal={closeModal}
          showNextImage={showNextImage}
          showPrevImage={showPrevImage}
        />

        <div className="itinerary-main-content">
          <div className="itinerary-details-column">
            <div className="trip-header-card">
              <h1 className="trip-title">{processedTourData.title}</h1>
              <div className="trip-meta">
                <span><FaCalendarAlt /> {processedTourData.duration} </span>
                {processedTourData.destinations.length > 0 && (
                  <span><FaMapMarkerAlt /> {processedTourData.destinations.join(' • ')}</span>
                )}
              </div>
              {processedTourData.brochure_url ? (
                <a href={processedTourData.brochure_url} target="_blank" rel="noopener noreferrer" className="download-itinerary-btn">
                  <FaDownload /> Download Itinerary
                </a>
              ) : (
                <button className="download-itinerary-btn" onClick={() => alert('No brochure available for download.')}>
                  <FaDownload /> Download Itinerary
                </button>
              )}
            </div>

            <ItineraryTabs
              tabsRef={tabsRef}
              activeTab={activeTab}
              scrollToSection={scrollToSection}
            />

            <ItineraryOverview
              overviewRef={overviewRef}
              overview={processedTourData.overview}
            />

            {processedTourData.days && processedTourData.days.length > 0 && (
              <ItineraryTimeline
                itineraryRef={itineraryRef}
                days={processedTourData.days}
                activeDay={activeDay}
                handleDayClick={handleDayClick}
              />
            )}

            <ItineraryDetails
              detailsRef={detailsRef}
              inclusions={processedTourData.inclusions}
              exclusions={processedTourData.exclusions}
            />

            {processedTourData.notes && processedTourData.notes.length > 0 && (
              <ItineraryNotes
                notesRef={notesRef}
                notes={processedTourData.notes}
              />
            )}

            {/* Render cancellation component with default data (no cancellationPolicy prop) */}
            <ItineraryCancellation
              cancellationRef={cancellationRef}
            />
          </div>

          <div className="booking-card-column">
            <ItineraryBookingCard
              pricingCategories={processedTourData.pricingCategories}
              lowestPriceIndex={processedTourData.lowestPriceIndex}
              selectedPricing={selectedPricing}
              handlePricingSelect={handlePricingSelect}
              originalPrice={processedTourData.originalPrice}
              discountPercent={processedTourData.discountPercent}
              formatPrice={formatPrice}
              tourBatches={processedTourData.tourBatches}
              activeMonth={activeMonth}
              visibleMonths={processedTourData.visibleMonths}
              hiddenMonths={processedTourData.hiddenMonths}
              handleMonthSelect={handleMonthSelect}
              getShortMonth={getShortMonth}
              isMoreDropdownOpen={isMoreDropdownOpen}
              setIsMoreDropdownOpen={setIsMoreDropdownOpen}
              selectedBatch={selectedBatch}
              handleBatchSelect={handleBatchSelect}
              tripTitle={processedTourData.title}
              tripDuration={processedTourData.duration}
            />
          </div>
        </div>

        {processedTourData.pricingCategories.length > 0 && (
          <div className="mobile-sticky-book-bar">
            <div className="mobile-sticky-price">
              <span className="mobile-sticky-price-label">Starting from</span>
              <span className="mobile-sticky-price-value">₹{formatPrice(processedTourData.displayPrice)}</span>
            </div>
            <button className="mobile-sticky-book-btn" onClick={toggleBookingForm}>
              Book Now
            </button>
          </div>
        )}

        {isBookingFormOpen && (
          <div className="mobile-booking-overlay" onClick={toggleBookingForm}>
            <div className="mobile-booking-form" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-booking-close">
                <button className="mobile-booking-close-btn" onClick={toggleBookingForm}>×</button>
              </div>
              <ItineraryBookingCard
                pricingCategories={processedTourData.pricingCategories}
                lowestPriceIndex={processedTourData.lowestPriceIndex}
                selectedPricing={selectedPricing}
                handlePricingSelect={handlePricingSelect}
                originalPrice={processedTourData.originalPrice}
                discountPercent={processedTourData.discountPercent}
                formatPrice={formatPrice}
                tourBatches={processedTourData.tourBatches}
                activeMonth={activeMonth}
                visibleMonths={processedTourData.visibleMonths}
                hiddenMonths={processedTourData.hiddenMonths}
                handleMonthSelect={handleMonthSelect}
                getShortMonth={getShortMonth}
                isMoreDropdownOpen={isMoreDropdownOpen}
                setIsMoreDropdownOpen={setIsMoreDropdownOpen}
                selectedBatch={selectedBatch}
                handleBatchSelect={handleBatchSelect}
                tripTitle={processedTourData.title}
                tripDuration={processedTourData.duration}
              />
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Itinerary;